import {
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
  MenuSectionLabel,
} from '@src/components/common/contextMenuStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

/** `EditorStore.transcriptSegmentArchitectureMigrationLog`, as read here. */
type MigrationLog = { status: string; debugInfo?: object } | null;

interface Props {
  /** The transcript-segment migration result the debug view reports. */
  migrationLog: MigrationLog;
  /** Opens the Debug Data modal (owned by the Stage). */
  onOpenDebugData: () => void;
}

const MENU_WIDTH = 300;
const MENU_MAX_HEIGHT = 360;

/**
 * PLAT-12095 — the Stage toolbar's debug kebab, last in the row after Voice /
 * Music. Holds what used to float over the toolbar in debug mode (the
 * migration status, its debug info and the DEBUG button), so nothing covers
 * the pickers. The Stage renders it only in debug mode.
 */
export const StageDebugMenu: React.FC<Props> = ({
  migrationLog,
  onOpenDebugData,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const { menuRef, style } = useAnchoredDropdown(
    open,
    triggerRef,
    close,
    MENU_MAX_HEIGHT,
    { width: MENU_WIDTH, align: 'end' },
  );

  const status = migrationLog?.status;
  const debugInfo = migrationLog?.debugInfo;
  const hasDebugInfo = !!debugInfo && Object.keys(debugInfo).length > 0;

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label="Debug"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="stage-debug-menu-trigger"
        onClick={() => setOpen(o => !o)}
      >
        <MoreVertIcon />
      </Trigger>
      {open &&
        style &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            style={style}
            role="menu"
            data-testid="stage-debug-menu"
          >
            <MenuSectionLabel>Migration Status</MenuSectionLabel>
            <StatusValue $status={status} data-testid="stage-debug-status">
              {status ?? 'Not recorded'}
            </StatusValue>
            <MenuSectionLabel>Debug Info</MenuSectionLabel>
            <DebugInfo data-testid="stage-debug-info">
              {hasDebugInfo ? JSON.stringify(debugInfo, null, 2) : 'None'}
            </DebugInfo>
            <ContextMenuItem
              role="menuitem"
              onClick={() => {
                close();
                onOpenDebugData();
              }}
            >
              Open Debug Data
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

// Square and centred in the toolbar row, flush with its 8px gap.
const Trigger = styled(KebabButton)`
  align-self: center;
  width: 28px;
  height: 28px;
  margin-left: 0;
  padding: 0;
`;

const StatusValue = styled.div<{ $status?: string }>`
  padding: 2px 10px 6px;
  font-size: var(--text-xs);
  color: ${({ $status }) => {
    if ($status === 'error') return 'var(--destructive)';
    if ($status === 'ok') return 'var(--primary)';
    if ($status === 'warning' || $status === 'not_supported')
      return 'var(--accent)';
    return 'var(--text-muted)';
  }};
`;

const DebugInfo = styled.pre`
  margin: 2px 10px 6px;
  font-family: var(--font-mono, monospace);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;
